import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, CalendarClock, Gift, Ticket, Trophy, RefreshCw, Wallet, Clock, Phone, FileBadge, Info, Hash, MapPin, MonitorSmartphone, MessageSquareText } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { TicketGrid, TicketGridSkeleton } from "../components/basar/TicketGrid";
import { PrizeGallery } from "../components/basar/PrizeGallery";
import { BuyPanel } from "../components/basar/BuyPanel";
import { MyTicketsDialog } from "../components/basar/MyTicketsDialog";
import { ShareMenu } from "../components/basar/ShareMenu";
import { GiveGiftDialog } from "../components/basar/GiveGiftDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { api, imageSrc, formatDateTime } from "../lib/api";
import { basarStatusMeta, drawDateTime, nok, shortDateTime, soldPercent } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const TICKETS_REFRESH_MS = 30000;

// Basarside: /basar/:id – premiar, rutenett med sjølvvalde nummer, kjøparskjema og Vipps. Etter trekning: vinnarliste.
export default function BasarPage() {
  const { id } = useParams();
  const { t } = useLang();
  const [basar, setBasar] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const timer = useRef(null);

  const loadBasar = useCallback(() => api.get(`/basar/${id}`).then((res) => setBasar(res.data.basar)), [id]);
  const loadTickets = useCallback(() => api.get(`/basar/${id}/tickets`).then((res) => setTickets(res.data)), [id]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setError(false);
    setSelected(new Set());
    window.scrollTo({ top: 0 });
    Promise.all([loadBasar(), loadTickets()])
      .catch((err) => (err?.response?.status === 404 ? setNotFound(true) : setError(true)))
      .finally(() => setLoading(false));
  }, [id, loadBasar, loadTickets]);

  // Periodisk oppdatering av selde/haldne nummer medan salet er ope (berre når fana er synleg)
  useEffect(() => {
    if (!basar || basar.status !== "open") return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") {
        loadTickets().catch(() => {});
        loadBasar().catch(() => {});
      }
    };
    timer.current = setInterval(tick, TICKETS_REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [basar?.status, basar, loadTickets, loadBasar]);

  const sold = useMemo(() => new Set(tickets?.sold || []), [tickets]);
  const held = useMemo(() => new Set(tickets?.held || []), [tickets]);
  const winners = useMemo(() => new Set(tickets?.winners || []), [tickets]);

  // Nummer som i mellomtida er selde/haldne av andre, fell ut av valet
  useEffect(() => {
    if (!tickets) return;
    setSelected((prev) => {
      const next = new Set([...prev].filter((n) => !sold.has(n) && !held.has(n)));
      return next.size === prev.size ? prev : next;
    });
  }, [tickets, sold, held]);

  const toggle = (n) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else {
        if (basar && next.size >= Number(basar.max_per_order || 100)) return prev;
        next.add(n);
      }
      return next;
    });
  };
  const remove = (n) => setSelected((prev) => { const next = new Set(prev); next.delete(n); return next; });
  const clear = () => setSelected(new Set());
  const replaceSelection = (numbers) => setSelected(new Set(numbers));
  const numbersTaken = (numbers) => {
    setSelected((prev) => { const next = new Set(prev); numbers.forEach((n) => next.delete(n)); return next; });
    loadTickets().catch(() => {});
  };
  const refresh = () => Promise.all([loadTickets(), loadBasar()]).catch(() => {});

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="basar-page-loading">
          <Skeleton className="h-56 w-full rounded-2xl" />
          <Skeleton className="h-9 w-2/3" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-6"><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}</div><TicketGridSkeleton /></div>
            <div className="lg:col-span-5"><Skeleton className="h-[520px] rounded-2xl" /></div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || error || !basar) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center space-y-4" data-testid={notFound ? "basar-not-found" : "basar-error-state"}>
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary text-muted-foreground"><Ticket className="w-7 h-7" /></span>
          <h1 className="text-2xl font-semibold">{notFound ? t("Fant ikke basaren") : t("Kunne ikke laste basaren")}</h1>
          <p className="text-sm text-muted-foreground">{notFound ? t("Basaren er ikke tilgjengelig lenger, eller lenken er feil.") : t("Prøv igjen om litt.")}</p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            {!notFound && <Button onClick={() => window.location.reload()} data-testid="basar-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button>}
            <Button asChild variant={notFound ? "default" : "outline"} data-testid="basar-not-found-overview-link"><Link to="/basar"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Alle basarer")}</Link></Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const st = basarStatusMeta(basar);
  const drawn = basar.status === "drawn" && basar.winners;
  const pct = soldPercent(basar.stats);
  const price = Number(basar.price || 0);
  const min = Math.max(1, Number(basar.min_per_buyer || 1));
  const count = selected.size;
  const total = count * price;
  const soldOut = basar.status === "open" && Number(basar.stats?.remaining ?? 0) === 0;
  const salesOpen = basar.status === "open" && basar.in_sale_period;
  // Rutenettet kan brukast (velje nummer) så lenge salet er ope og det finst ledige lodd – òg utan Vipps (kjøp hos arrangøren)
  const selectable = salesOpen && !soldOut;
  const winnersByPrize = drawn ? Object.fromEntries(basar.winners.map((w) => [w.prize_id, w.winners])) : null;
  const totalWinners = drawn ? basar.winners.reduce((s, w) => s + (w.winners?.length || 0), 0) : 0;

  const scrollToBuy = () => document.getElementById("basar-buy-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Statuskort når ein ikkje kan kjøpe (stengt, trekt, utanfor periode, utselt, Vipps manglar)
  const unavailableCard = () => {
    if (drawn) {
      return (
        <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-status-drawn">
          <Trophy className="h-4 w-4" />
          <AlertTitle>{t("Trekningen er gjennomført")}</AlertTitle>
          <AlertDescription>{t("Vinnerlisten står under premiene. Takk til alle som støttet!")}{basar.draw_published_at ? ` ${t("Publisert {date}.", { date: formatDateTime(basar.draw_published_at) })}` : ""}</AlertDescription>
        </Alert>
      );
    }
    if (basar.status === "closed") {
      return (
        <Alert data-testid="basar-status-closed">
          <Clock className="h-4 w-4" />
          <AlertTitle>{t("Loddsalget er stengt")}</AlertTitle>
          <AlertDescription>{basar.draw_info || t("Trekningen kunngjøres av arrangøren. Vinnerne publiseres her.")}</AlertDescription>
        </Alert>
      );
    }
    if (basar.status === "open" && !basar.in_sale_period) {
      const upcoming = basar.sale_start && new Date(basar.sale_start) > new Date();
      return (
        <Alert className="bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/30" data-testid="basar-status-outside-period">
          <CalendarClock className="h-4 w-4" />
          <AlertTitle>{upcoming ? t("Loddsalget åpner snart") : t("Loddsalget er avsluttet")}</AlertTitle>
          <AlertDescription>{upcoming ? t("Salget starter {date}.", { date: shortDateTime(basar.sale_start) }) : t("Salgsperioden er over. Trekningen kunngjøres av arrangøren.")}</AlertDescription>
        </Alert>
      );
    }
    if (soldOut) {
      return (
        <Alert className="bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30" data-testid="basar-status-sold-out">
          <Ticket className="h-4 w-4" />
          <AlertTitle>{t("Alle lodd er solgt!")}</AlertTitle>
          <AlertDescription>{basar.draw_info || t("Trekningen kunngjøres av arrangøren.")}</AlertDescription>
        </Alert>
      );
    }
    return null;
  };

  return (
    <PublicLayout>
      <div className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-8 ${basar.can_buy ? "pb-28 lg:pb-10" : ""}`} data-testid="basar-detail-page" data-status={basar.status}>
        <Link to="/basar" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="basar-back-link">
          <ArrowLeft className="w-4 h-4" /> {t("Alle basarer")}
        </Link>

        {/* Topp: bilete + tittel + nøkkelinfo */}
        <motion.header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-5">
          {basar.image_url && (
            <div className="relative aspect-[21/9] sm:aspect-[3/1] rounded-2xl overflow-hidden bg-muted border border-border" data-testid="basar-hero-image">
              <img src={imageSrc(basar.image_url)} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={st.className} data-testid="basar-status-badge">{st.label}</Badge>
                <Link to={`/arrangor/${basar.organizer_id}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors duration-150" data-testid="basar-organizer-link">
                  <Building2 className="w-4 h-4" /> {basar.organizer_name}
                </Link>
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" data-testid="basar-title">{basar.name}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 tabular-nums" data-testid="basar-price"><Ticket className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("{amount} per lodd", { amount: nok(price) })}</span>
                <span className="inline-flex items-center gap-1.5 tabular-nums" data-testid="basar-series"><Hash className="w-4 h-4" /> {t("Nummer {from}–{to}", { from: basar.series_from, to: basar.series_to })}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="basar-prize-count"><Gift className="w-4 h-4" /> {basar.prizes?.length === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: basar.prizes?.length || 0 })}</span>
                {basar.draw_at ? (
                  <span className="inline-flex items-center gap-1.5" data-testid="basar-draw-at"><CalendarClock className="w-4 h-4" /> {t("Trekning {date}", { date: drawDateTime(basar.draw_at) })}</span>
                ) : basar.draw_info ? (
                  <span className="inline-flex items-center gap-1.5" data-testid="basar-draw-info"><CalendarClock className="w-4 h-4" /> {basar.draw_info}</span>
                ) : null}
                {basar.sale_end && salesOpen && <span className="inline-flex items-center gap-1.5" data-testid="basar-sale-end"><Clock className="w-4 h-4" /> {t("Salget slutter {date}", { date: shortDateTime(basar.sale_end) })}</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <ShareMenu basarId={basar.id} />
              <MyTicketsDialog basarId={basar.id} drawn={!!drawn} />
              {basar.can_gift && <GiveGiftDialog basar={basar} />}
              {selectable && <Button onClick={scrollToBuy} className="lg:hidden" data-testid="basar-header-buy-button"><Wallet className="w-4 h-4 mr-1.5" /> {basar.can_buy ? t("Kjøp lodd") : t("Velg lodd")}</Button>}
            </div>
          </div>
          <div className="space-y-1.5 max-w-xl" data-testid="basar-progress-block">
            <Progress value={pct} className="h-2.5 bg-[hsl(var(--warning))]/15 [&>div]:bg-[hsl(var(--warning))]" aria-label={t("Solgte lodd")} data-testid="basar-progress" />
            <p className="text-sm text-muted-foreground tabular-nums" data-testid="basar-stats">
              {t("Solgt {sold} av {total} lodd", { sold: basar.stats?.sold ?? 0, total: basar.stats?.total ?? 0 })}
              {basar.status === "open" ? ` · ${t("{n} ledige", { n: basar.stats?.remaining ?? 0 })}` : ""}
              {basar.stats?.held > 0 ? ` · ${t("{n} reservert", { n: basar.stats.held })}` : ""}
            </p>
          </div>
          {basar.description && <p className="text-[15px] leading-relaxed whitespace-pre-line text-foreground/90 max-w-3xl" data-testid="basar-description">{basar.description}</p>}
        </motion.header>

        {/* Hovudgrid: venstre premiar + rutenett, høgre kjøpspanel (sticky) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="lg:col-span-7 space-y-8 min-w-0">
            <section className="space-y-4" id="premier" data-testid="basar-prizes-section">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-heading font-semibold tracking-tight flex items-center gap-2"><Gift className="w-5 h-5 text-[hsl(var(--warning))]" /> {drawn ? t("Premier og vinnere") : t("Premier")}</h2>
                {drawn && <Badge className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))] tabular-nums" data-testid="basar-winner-count">{totalWinners === 1 ? t("{n} vinner", { n: 1 }) : t("{n} vinnere", { n: totalWinners })}</Badge>}
              </div>
              {basar.prizes?.length ? <PrizeGallery prizes={basar.prizes} winnersByPrize={winnersByPrize} /> : <p className="text-sm text-muted-foreground" data-testid="basar-prizes-empty">{t("Premiene publiseres snart.")}</p>}
            </section>

            <section className="space-y-4" id="lodd" data-testid="basar-grid-section">
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-xl font-heading font-semibold tracking-tight flex items-center gap-2"><Ticket className="w-5 h-5 text-[hsl(var(--warning))]" /> {drawn ? t("Loddene") : t("Velg loddnummer")}</h2>
                  {selectable && basar.can_buy && <Badge variant="outline" className="shrink-0 tabular-nums" data-testid="basar-grid-step-badge">{t("Steg {n} av {total}", { n: 1, total: 3 })}</Badge>}
                </div>
                {selectable && (
                  <p className="text-sm text-muted-foreground" data-testid="basar-grid-hint">
                    {basar.can_buy
                      ? t("Trykk på ledige nummer for å velge eller fjerne dem. Valget lagres ikke for seg – numrene holdes av for deg først når du trykker «Betal med Vipps» i kjøpspanelet.")
                      : t("Trykk på ledige nummer for å velge dem, og kontakt arrangøren for å kjøpe.")}
                  </p>
                )}
              </div>
              <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                {tickets ? (
                  <TicketGrid seriesFrom={basar.series_from} seriesTo={basar.series_to} sold={sold} held={held} winners={winners} selected={selected} onToggle={toggle} disabled={!selectable} maxSelectable={Number(basar.max_per_order || 100)} asOf={tickets.as_of} onRefresh={refresh} />
                ) : (
                  <TicketGridSkeleton />
                )}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-5 space-y-4 lg:sticky lg:top-24">
            {selectable ? (
              <BuyPanel basar={basar} selected={selected} onRemove={remove} onClear={clear} onReplaceSelection={replaceSelection} onNumbersTaken={numbersTaken} />
            ) : (
              unavailableCard()
            )}
            <div className="rounded-2xl border border-border bg-card p-5 space-y-3 text-sm" data-testid="basar-info-card">
              <p className="font-heading font-semibold">{t("Om basaren")}</p>
              <p className="flex items-start gap-2 text-muted-foreground"><Building2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{t("Arrangeres av")} <Link to={`/arrangor/${basar.organizer_id}`} className="text-foreground underline underline-offset-4 hover:text-foreground/80 transition-colors duration-150">{basar.organizer_name}</Link></span></p>
              {basar.contact_info && <p className="flex items-start gap-2 text-muted-foreground" data-testid="basar-contact-info"><Phone className="w-4 h-4 mt-0.5 shrink-0" /><span className="break-words">{basar.contact_info}</span></p>}
              {(basar.draw_at || basar.draw_info) && (
                <p className="flex items-start gap-2 text-muted-foreground" data-testid="basar-info-draw"><CalendarClock className="w-4 h-4 mt-0.5 shrink-0" /><span>{basar.draw_at ? t("Trekning {date}", { date: drawDateTime(basar.draw_at) }) : ""}{basar.draw_at && basar.draw_info ? " · " : ""}{basar.draw_info}</span></p>
              )}
              {basar.draw_place && <p className="flex items-start gap-2 text-muted-foreground" data-testid="basar-info-place"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /><span>{basar.draw_place}</span></p>}
              {basar.draw_electronic && <p className="flex items-start gap-2 text-muted-foreground" data-testid="basar-info-electronic"><MonitorSmartphone className="w-4 h-4 mt-0.5 shrink-0" /><span>{t("Elektronisk trekning – vinnerne publiseres her på basarsiden.")}</span></p>}
              {basar.poster_note && <p className="flex items-start gap-2 text-foreground/90 rounded-lg bg-[hsl(var(--warning))]/10 px-3 py-2" data-testid="basar-info-note"><MessageSquareText className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--warning))]" /><span>{basar.poster_note}</span></p>}
              {basar.permit_ref && <p className="flex items-start gap-2 text-muted-foreground" data-testid="basar-permit-ref"><FileBadge className="w-4 h-4 mt-0.5 shrink-0" /><span>{t("Lotteritillatelse / referanse: {ref}", { ref: basar.permit_ref })}</span></p>}
              <p className="flex items-start gap-2 text-xs text-muted-foreground pt-1 border-t border-border"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{t("Right in formidler bare loddkjøpet. Arrangøren er ansvarlig for basaren, premiene og eventuell lotteritillatelse. Betalingen går direkte til arrangøren.")}</span></p>
            </div>
            {basar.can_gift && (
              <div className="rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-5 space-y-3" data-testid="basar-gift-card">
                <p className="font-heading font-semibold flex items-center gap-2"><Gift className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Vil du gi en premie?")}</p>
                <p className="text-sm text-muted-foreground">{t("Privatpersoner og bedrifter kan støtte basaren med en pengegave{vipps} eller en gjenstand. Gaven blir med i premielisten med en gang – giverens navn vises ikke offentlig.", { vipps: basar.can_gift_money ? ` (${t("Vipps")})` : "" })}</p>
                <GiveGiftDialog basar={basar} trigger={<Button variant="secondary" className="w-full sm:w-auto" data-testid="basar-gift-card-button"><Gift className="w-4 h-4 mr-1.5 text-[hsl(var(--warning))]" /> {t("Gi premie")}</Button>} />
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Sticky kjøpsoppsummering på mobil */}
      {basar.can_buy && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80" data-testid="basar-sticky-summary">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium tabular-nums" data-testid="basar-sticky-count">{count === 1 ? t("{n} lodd valgt", { n: 1 }) : t("{n} lodd valgt", { n: count })}</p>
              <p className="text-xs text-muted-foreground tabular-nums truncate" data-testid="basar-sticky-total">
                {count < min
                  ? `${t("Steg {n} av {total}", { n: 1, total: 3 })} · ${t("Velg minst {n} lodd", { n: min })}`
                  : `${t("Steg {n} av {total}", { n: 2, total: 3 })} · ${t("Sum {amount}", { amount: nok(total) })}`}
              </p>
            </div>
            <Button onClick={scrollToBuy} disabled={count < min} className="shrink-0" data-testid="basar-sticky-pay-button"><Wallet className="w-4 h-4 mr-1.5" /> {t("Til betaling")}</Button>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}
